// src/components/ui/StoreCard/StoreCard.jsx - Restored original styling with favorites API support
import React, { useState } from "react";
import { ArrowRightSquare, ArrowUpRightFromSquare, Heart, HeartIcon } from "lucide-react";
import { useNavigate } from "react-router";
import storeService from "../../../services/storeService";
import { useAuth } from "../../../context/AuthContext";
import { parseISO, parse, format, compareAsc } from 'date-fns';
import { TZDate } from "@date-fns/tz";
import { getNzTDateTimeFromCollectionWindow, secondsUntilFutureClose } from "../../../utils";

const DUMMY_IMAGE_URL = "/img/sample.jpeg";


// Utility function to format distance (minimal change)
const formatDistance = (distance) => {
  if (!distance && distance !== 0) return '0 m';

  const numericDistance = parseFloat(distance);

  if (isNaN(numericDistance)) return '0 m';

  // If distance is greater than or equal to 1000 meters, show in kilometers
  if (numericDistance >= 1000) {
    const kilometers = numericDistance / 1000;
    return `${kilometers.toFixed(1)} km`;
  }

  // If distance is less than 1000 meters, show in meters
  return `${Math.round(numericDistance)} m`;
};

const sortByClosingSoon = (a, b) => {
  const nowNz = new TZDate(new Date(), "Pacific/Auckland");

  const ra = secondsUntilFutureClose(a, nowNz);
  const rb = secondsUntilFutureClose(b, nowNz);

  return ra - rb
};

const StoreCard = ({
  isMap = false,
  name,
  store_type,
  image_url,
  tags = [],
  rating_avg,
  is_favorite: initialIsFavorite,
  products, // Use products now. And check for stock and collection window in frontend
  distance,
  public_id,
  onFavoriteToggle,
  showBadge = false,
}) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [isToggling, setIsToggling] = useState(false);
  const rating = rating_avg || 0;
  const { user } = useAuth();

  const nzTimeNow = new TZDate(new Date(), "Pacific/Auckland");

  let productData = {};
  let badgeType = "";
  if (products && Array.isArray(products) && products.length > 0) {
    const inStockProducts = products.filter(product => {
      const endTime = parse(product.collection_window.end_time, 'HH:mm:ss', new Date());
      const endTimeNz = new TZDate(endTime, "Pacific/Auckland");

      if (compareAsc(endTimeNz, nzTimeNow) < 0) {
        return false;
      }
      return product.qty_remaining > 0;
    })
      .sort((a, b) => {
        const timeA = parseISO(a.collection_window.date);
        const timeB = parseISO(b.collection_window.date);
        return timeA - timeB;
      })


    const urgentProduct = inStockProducts.find((p) => {
      const { start, end } = getNzTDateTimeFromCollectionWindow(p.collection_window);
      // start time is in the past
      if (compareAsc(start, nzTimeNow) <= 0) {
        const timeDiffMs = end.getTime() - nzTimeNow.getTime();
        // if closing within 60 minutes
        if (timeDiffMs > 0 && timeDiffMs <= 3600 * 1000) {
          return true;
        }
      }
      return false;
    });

    const activeProduct = inStockProducts.find((p) => {
      const { start, end } = getNzTDateTimeFromCollectionWindow(p.collection_window);
      // end time is in the past, we dont want this, this is long gone
      if (compareAsc(end, nzTimeNow) < 0) {
        return false;
      }
      // start time is in the future, we filter this out.
      if (compareAsc(start, nzTimeNow) > 0) {
        return false;
      }
      return true
    });


    if (urgentProduct) {
      productData = urgentProduct;
    }
    else if (activeProduct) {
      productData = activeProduct;
    }
    else if (!urgentProduct && !activeProduct && inStockProducts.length > 0) {
      productData = inStockProducts[0];
    }

    if (showBadge) {
      if (urgentProduct) {
        badgeType = 'urgent';
      }
      else if (activeProduct) {
        badgeType = 'active'
      }
    }



  }




  // todo: storeCard will take a sort parameter, cheapest versus available next will be driven by the parent
  // and then products will be sorted accordingly

  const collectionWindowData = productData.collection_window;
  const pricingData = productData.pricing;

  const handleFavoriteToggle = async (e) => {
    e.stopPropagation(); // Prevent card click when clicking favorite

    if (isToggling) return; // Prevent multiple simultaneous requests

    setIsToggling(true);
    try {
      await storeService.toggleFavorite(public_id);
      const newFavoriteState = !isFavorite;
      setIsFavorite(newFavoriteState);

      // Call the parent callback if provided
      if (onFavoriteToggle) {
        onFavoriteToggle(public_id, newFavoriteState);
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      // Revert the UI state on error
      setIsFavorite(initialIsFavorite);
    } finally {
      setIsToggling(false);
    }
  };

  const handleCardClick = (e) => {
    // Navigate to store details page
    if (isMap) {
      navigate(`/store/${public_id}`, {
        state: { from: "map" },
      });
      return
    }


    navigate(`/store/${public_id}`);

  };

  // console.log("Rendering StoreCard:", { name, isFavorite, initialIsFavorite, productData, product });


  const formatTime = (startOrEndCollectionTime) => {
    const time = parse(startOrEndCollectionTime, 'HH:mm:ss', new Date());
    return format(time, 'hh:mm a');
  }
  return (
    <div
      className="font-sans relative w-full bg-white rounded-md"
      onClick={handleCardClick}
    >
      <div className="relative">
        {isMap ? <></> : (
          <>
            <img
              src={productData.image || image_url || DUMMY_IMAGE_URL}
              alt={name}
              className={`w-full aspect-5/3 object-cover rounded-tl-md rounded-tr-md ${!!productData.public_id ? 'cursor-pointer' : 'opacity-40 pointer-events-none'}`}
            />
            {
              !isMap && showBadge && badgeType ? <span className={`absolute top-2 right-2 font-gg-medium text-sm rounded px-3 py-1 cursor-pointer ${badgeType === 'urgent' ? 'bg-gg-green text-white' : badgeType === 'active' ? 'bg-gg-dark text-white' : ''}`}>
                {badgeType === 'active' ? 'Pick up now' : badgeType === 'urgent' ? "Before it's too late" : ''}
              </span>
                : <></>
            }

            {/*
            tags.length > 0 && (
              <span className="absolute top-2 left-2 font-gg-medium text-xs rounded-md px-3 py-1 shadow bg-gg-purple">
                {tags[0].charAt(0).toUpperCase() + tags[0].slice(1)}
              </span>
            )
            */
            }
            { /*
            <span className="absolute top-2 right-2 bg-[rgba(255,255,255,0.7)] text-gg-green font-bold text-sm rounded-lg p-1 flex items-center">
              <span className="text-xs font-gg-body mr-1">★</span>
              <span className="text-xs font-gg-body">{rating.toFixed(1)}</span>
            </span>
            */
            }
          </>
        )}
      </div>
      <div className="py-2 px-4">
        <div className="flex justify-between">
          <div className="flex w-full justify-between items-center cursor-pointer">
            <h2 className="font-gg-medium text-xl text-gg-dark whitespace-nowrap overflow-hidden text-ellipsis max-w-10/12">{name}</h2>
            {isMap ?
              <p className="font-gg-medium text-gg-green underline text-base">View</p>
              : <></>
            }
          </div>
          {!isMap ?
            <div
              className={`cursor-pointer z-10 ${isToggling ? "opacity-50" : ""}`}
              onClick={handleFavoriteToggle}
            >
              {user ? <HeartIcon size={24} className={isFavorite ? " stroke-red-400 fill-red-400" : "stroke-gg-dark"} /> : <></>}
            </div>
            : <></>
          }
        </div>
        <p className="text-base leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-full">{isMap && (!productData || !productData.title) ? "Not available today" : productData?.title ?? ""}</p>
        {collectionWindowData ? (
          <p className="text-base leading-tight">
            Pickup:{" "}
            {formatTime(collectionWindowData.start_time)} - {" "}
            {formatTime(collectionWindowData.end_time)}
            <span className="text-base"> | </span>
            <span className="text-base">{formatDistance(distance) || distance}</span>
          </p>
        ) : (
          isMap ?
            <p className="text-base leading-tight"><span className="text-base">{formatDistance(distance) || distance}</span></p> : <></>
        )}
        {pricingData && (
          <div className={"flex items-center justify-end"} >
            <span className="text-black opacity-30 line-through mr-1">
              $
              {parseFloat(
                pricingData.rrp || pricingData.original_price || 0,
              ).toFixed(2)}
            </span>
            <span className="text-2xl font-gg-medium">
              ${parseFloat(pricingData.discounted_price || 0).toFixed(2)}
            </span>
          </div>
        )}
        {/* Favorite button positioned absolutely in bottom right */}

      </div>
    </div >
  );
};

export default StoreCard;